import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

export type TThirdPartyInsuranceStep =
    | "plate"           // استپ ۱: پلاک خودرو و بیمه‌گذار
    | "carInfo"         // استپ ۲: اطلاعات خودرو
    | "prevInsurance"   // استپ ۳: اطلاعات بیمه‌نامه قبلی
    | "coverage";        // استپ ۴: پوشش مالی و انتقال تخفیف

/** ساختار پلاک ایران: دو رقم - حرف - سه رقم - دو رقم (کد شهر) */
export interface IPlate {
    part1: string;   // دو رقم سمت راست
    part2: string;  // حرف پلاک (مثلا "ب")
    part3: string;   // سه رقم وسط
    part4: string;   // دو رقم کد شهر / ایران
}

/* ----------------------------- Step 1: Plate ----------------------------- */

export interface IInsurerInfo {
    isAnotherPerson: boolean; // چک‌باکس "بیمه‌گذار شخص دیگری است"
    ownerMobile: string;      // فعال فقط وقتی isAnotherPerson=false (بیمه‌گذار خودِ کاربره)
    nationalCode: string;     // فعال فقط وقتی isAnotherPerson=true (بیمه‌گذار شخص دیگریه)
    anotherMobile: string
}

export interface IPlateStep {
    plate: IPlate;
    ownerNationalCode: string;
    insurer: IInsurerInfo;
}

/* ---------------------------- Step 2: Car Info --------------------------- */

export interface ICarInfoStep {
    usageType: string;  // کاربری
    brand: string;      // برند
    type: string;       // تیپ
    fuelType: string;   // نوع سوخت
    color: string;      // رنگ
    yearModel: string;  // سال ساخت / مدل
    category: string // نوع
}

/* ------------------------ Step 3: Previous Insurance ---------------------- */

export interface IDamageHistory {
    financialDamageCount: string; // تعداد خسارت مالی
    physicalDamageCount: string;  // تعداد خسارت جانی
    driverAccidentCount: string;  // تعداد خسارت حوادث راننده
}

export interface IPrevInsuranceStep {
    insuranceCompany: string; // شرکت بیمه‌گر
    startDate: string;        // تاریخ شروع
    endDate: string;          // تاریخ پایان
    thirdPartyDiscount: string; // تخفیف ثالث
    driverDiscount: string;     // تخفیف راننده
    hadDamageLastYear: boolean; // چک‌باکس "آیا در سال بیمه‌نامه گذشته خسارت داشتید؟"
    damageHistory: IDamageHistory; // فعال فقط وقتی hadDamageLastYear=true
}

/* --------------------------- Step 4: Coverage ----------------------------- */

export interface ITransferDiscount {
    plate: IPlate;
    nationalCode: string;
    thirdPartyDiscount: string; // تخفیف ثالث پلاک انتقالی
    driverDiscount: string;     // تخفیف راننده پلاک انتقالی
}

export interface ICoverageStep {
    validityPeriod: string;       // مدت اعتبار
    financialCoverage: number;    // تعیین پوشش مالی (تومان)
    wantsDiscountTransfer: boolean; // چک‌باکس "آیا قصد انتقال تخفیف دارید؟"
    transfer: ITransferDiscount;    // فعال فقط وقتی wantsDiscountTransfer=true
}

/* ------------------------------- Root State -------------------------------- */

export interface IThirdPartyInsuranceState {
    step: TThirdPartyInsuranceStep;
    plateStep: IPlateStep;
    carInfoStep: ICarInfoStep;
    prevInsuranceStep: IPrevInsuranceStep;
    coverageStep: ICoverageStep;
}

/* -------------------------------------------------------------------------- */
/* Initial State                                                              */
/* -------------------------------------------------------------------------- */

const emptyPlate: IPlate = {
    part1: "",
    part2: "",
    part3: "",
    part4: ""
};

const initialState: IThirdPartyInsuranceState = {
    step: "plate",
    plateStep: {
        plate: { ...emptyPlate },
        ownerNationalCode: "",
        insurer: {
            isAnotherPerson: false,
            ownerMobile: "",
            anotherMobile: "",
            nationalCode: ""
        }
    },
    carInfoStep: {
        usageType: "",
        category: "",
        brand: "",
        type: "",
        fuelType: "",
        color: "",
        yearModel: ""
    },
    prevInsuranceStep: {
        insuranceCompany: "",
        startDate: "",
        endDate: "",
        thirdPartyDiscount: "",
        driverDiscount: "",
        hadDamageLastYear: false,
        damageHistory: {
            financialDamageCount: "",
            physicalDamageCount: "",
            driverAccidentCount: ""
        }
    },
    coverageStep: {
        validityPeriod: "",
        financialCoverage: 70_000_000,
        wantsDiscountTransfer: false,
        transfer: {
            plate: { ...emptyPlate },
            nationalCode: "",
            thirdPartyDiscount: "",
            driverDiscount: ""
        }
    }
};

/* -------------------------------------------------------------------------- */
/* Slice                                                                      */
/* -------------------------------------------------------------------------- */

const ThirdPartyInsuranceSlice = createSlice({
    name: "thirdPartyInsurance",
    initialState,
    reducers: {
        // ----- Navigation -----
        setStep: (state, action: PayloadAction<TThirdPartyInsuranceStep>) => {
            state.step = action.payload;
        },

        // ----- Step 1: Plate -----
        setPlate: (state, action: PayloadAction<Partial<IPlate>>) => {
            state.plateStep.plate = { ...state.plateStep.plate, ...action.payload };
        },
        setOwnerNationalCode: (state, action: PayloadAction<string>) => {
            state.plateStep.ownerNationalCode = action.payload;
        },
        setIsInsurerAnotherPerson: (state, action: PayloadAction<boolean>) => {
            state.plateStep.insurer.isAnotherPerson = action.payload;
            if (action.payload) {
                // تیک روشن شد (بیمه‌گذار شخص دیگریه) -> شماره موبایل لازم نیست
                state.plateStep.insurer.ownerMobile = "";
            } else {
                // تیک خاموش شد (بیمه‌گذار خودِ کاربره) -> کدملی بیمه‌گذار لازم نیست
                state.plateStep.insurer.nationalCode = "";
                state.plateStep.insurer.anotherMobile = "";
            }
        },
        setOwnerMobile: (state, action: PayloadAction<string>) => {
            state.plateStep.insurer.ownerMobile = action.payload;
        },
        setInsurerNationalCode: (state, action: PayloadAction<string>) => {
            state.plateStep.insurer.nationalCode = action.payload;
        },
        setInsureranotherMobile: (state, action: PayloadAction<string>) => {
            state.plateStep.insurer.anotherMobile = action.payload;
        },

        // ----- Step 2: Car Info -----
        setCarInfo: (state, action: PayloadAction<Partial<ICarInfoStep>>) => {
            state.carInfoStep = { ...state.carInfoStep, ...action.payload };
        },

        // ----- Step 3: Previous Insurance -----
        setPrevInsurance: (
            state,
            action: PayloadAction<
                Partial<Omit<IPrevInsuranceStep, "hadDamageLastYear" | "damageHistory">>
            >
        ) => {
            state.prevInsuranceStep = { ...state.prevInsuranceStep, ...action.payload };
        },
        setHadDamageLastYear: (state, action: PayloadAction<boolean>) => {
            state.prevInsuranceStep.hadDamageLastYear = action.payload;
            if (!action.payload) {
                state.prevInsuranceStep.damageHistory = {
                    financialDamageCount: "",
                    physicalDamageCount: "",
                    driverAccidentCount: ""
                };
            }
        },
        setDamageHistory: (state, action: PayloadAction<Partial<IDamageHistory>>) => {
            state.prevInsuranceStep.damageHistory = {
                ...state.prevInsuranceStep.damageHistory,
                ...action.payload
            };
        },

        // ----- Step 4: Coverage -----
        setValidityPeriod: (state, action: PayloadAction<string>) => {
            state.coverageStep.validityPeriod = action.payload;
        },
        setFinancialCoverage: (state, action: PayloadAction<number>) => {
            state.coverageStep.financialCoverage = action.payload;
        },
        setWantsDiscountTransfer: (state, action: PayloadAction<boolean>) => {
            state.coverageStep.wantsDiscountTransfer = action.payload;
            if (!action.payload) {
                state.coverageStep.transfer = {
                    plate: { ...emptyPlate },
                    nationalCode: "",
                    thirdPartyDiscount: "",
                    driverDiscount: ""
                };
            }
        },
        setTransferPlate: (state, action: PayloadAction<Partial<IPlate>>) => {
            state.coverageStep.transfer.plate = {
                ...state.coverageStep.transfer.plate,
                ...action.payload
            };
        },
        setTransferInfo: (
            state,
            action: PayloadAction<Partial<Omit<ITransferDiscount, "plate">>>
        ) => {
            state.coverageStep.transfer = { ...state.coverageStep.transfer, ...action.payload };
        },

        // ----- Reset -----
        resetThirdPartyInsuranceForm: () => initialState
    }
});

export const {
    setStep,
    setPlate,
    setOwnerNationalCode,
    setOwnerMobile,
    setIsInsurerAnotherPerson,
    setInsurerNationalCode,
    setCarInfo,
    setPrevInsurance,
    setHadDamageLastYear,
    setDamageHistory,
    setValidityPeriod,
    setInsureranotherMobile,
    setFinancialCoverage,
    setWantsDiscountTransfer,
    setTransferPlate,
    setTransferInfo,
    resetThirdPartyInsuranceForm
} = ThirdPartyInsuranceSlice.actions;

export default ThirdPartyInsuranceSlice.reducer;